import React, { useState, useEffect, useCallback } from 'react';
import { format, addDays, subDays, startOfWeek, isSameDay, isToday, isPast, startOfDay } from 'date-fns';
import { fr, enUS, ar } from 'date-fns/locale';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { formatPrice } from '../../config/currencies';
import './SlotPicker.css';

/**
 * SlotPicker – sélecteur de créneaux horaires.
 *
 * Reprend exactement le langage visuel d'AvailabilityCalendar.
 *
 * Props :
 *   listing          – objet listing (id, booking_mode, booking_settings, currency…)
 *   onSelectionChange – ({ selectedSlots, startDate, endDate, valid, reason }) => void
 *   quantity_requested
 *   seats_requested
 */
// moved inside component to allow i18n usage

export default function SlotPicker({
  listing,
  onSelectionChange,
  quantity_requested = 1,
  seats_requested = 1,
}) {
  const { t, i18n } = useTranslation('booking');

  const dateLocale = (i18n && i18n.language && i18n.language.startsWith('fr')) ? fr
    : (i18n && i18n.language && i18n.language.startsWith('ar')) ? ar
    : enUS;

  // DAYS_SHORT will be computed from the current weekDays and dateLocale below
  const bs = listing?.booking_settings || {};
  // Toujours parser en entier — les valeurs peuvent être des strings (saisie formulaire)
  const minDuration  = parseInt(bs.min_duration,  10) || 1;
  const maxDuration  = bs.max_duration != null ? (parseInt(bs.max_duration, 10) || null) : null;
  const unit         = bs.unit          || 'slot';
  const slotDuration = parseInt(bs.slot_duration, 10) || 60;
  const openingHours = bs.opening_hours || [];
  const bufferBefore = parseInt(bs.buffer_before, 10) || 0;
  const bufferAfter  = parseInt(bs.buffer_after,  10) || 0;
  const capacityMode = bs.capacity_mode || 'exclusive';
  const isCapacity   = listing?.booking_mode === 'time_slot_capacity';
  const currency     = listing?.currency || 'EUR';

  const formatMoney = (v) => formatPrice(v, currency);

  // ── Jour fermé ? (opening_hours défini et ne couvre pas ce jour ISO) ─────
  const isDayClosed = (day) => {
    if (openingHours.length === 0) return false; // pas de restriction → toujours ouvert
    // date-fns getDay : 0=dim, 1=lun… → convertir en ISO (1=lun…7=dim)
    const dowDateFns = day.getDay(); // 0=Sun
    const isoDay = dowDateFns === 0 ? 7 : dowDateFns;
    return !openingHours.some(h => h.day === isoDay);
  };

  // ── État : semaine affichée (ancre = lundi) ────────────────────────────────
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [activeDay, setActiveDay] = useState(() => {
    // Trouver le premier jour non passé ET ouvert dans les 60 prochains jours
    for (let i = 0; i < 60; i++) {
      const d = startOfDay(addDays(new Date(), i));
      if (!isDayClosed(d)) return d;
    }
    return startOfDay(new Date());
  });

  // ── État : créneaux du jour ────────────────────────────────────────────────
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── État : sélection ──────────────────────────────────────────────────────
  const [selectedSlots, setSelectedSlots] = useState([]);

  // ── Les 7 jours de la semaine ─────────────────────────────────────────────
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const DAYS_SHORT = [
    t('sidebar.calendar.weekdays.monday', 'L'),
    t('sidebar.calendar.weekdays.tuesday', 'M'),
    t('sidebar.calendar.weekdays.wednesday', 'M'),
    t('sidebar.calendar.weekdays.thursday', 'J'),
    t('sidebar.calendar.weekdays.friday', 'V'),
    t('sidebar.calendar.weekdays.saturday', 'S'),
    t('sidebar.calendar.weekdays.sunday', 'D'),
  ];

  // ── Charger les créneaux du jour actif ────────────────────────────────────
  const loadSlots = useCallback(async (day) => {
    if (!listing?.id) return;
    const dateStr = format(day, 'yyyy-MM-dd');
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/listings/${listing.id}/booking-engine/slots`, {
        params: {
          from: dateStr,
          to: dateStr,
          quantity_requested,
          seats_requested,
        },
      });
      setSlots(Array.isArray(data?.data?.slots) ? data.data.slots : []);
    } catch {
        setSlots([]);
        setError(t('settings.slotPicker.loadError', 'Impossible de charger les créneaux. Vérifiez votre connexion.'));
    } finally {
      setLoading(false);
    }
  }, [listing?.id, quantity_requested, seats_requested]);

  // Load slots when day or fetch params change
  useEffect(() => {
    loadSlots(activeDay);
  }, [activeDay, loadSlots]);

  // Clear selection ONLY when the active day actually changes (not when loadSlots ref changes
  // due to quantity_requested/seats_requested prop changes — that would wipe the selection
  // while the SlotPicker stays mounted but hidden via display:none).
  useEffect(() => {
    setSelectedSlots([]);
  }, [activeDay]);

  // ── Validation de la sélection ────────────────────────────────────────────
  const validate = useCallback((selection) => {
    if (selection.length === 0) {
      return { valid: false, reason: null }; // rien sélectionné = pas encore en erreur
    }
    if (selection.length < minDuration) {
      const label = unit === 'hour' ? t('settings.slotPicker.unitHour', 'heure(s)') : unit === 'slot' ? t('settings.slotPicker.unitSlot', 'créneau(x)') : t('settings.slotPicker.unitDay', 'jour(s)');
      return {
        valid: false,
        reason: t('settings.slotPicker.minRequired', 'Minimum {{min}} {{unit}} consécutif(s) requis ({{current}}/{{min}})', { min: minDuration, unit: label, current: selection.length }),
      };
    }
    if (maxDuration && selection.length > maxDuration) {
      const label = unit === 'hour' ? t('settings.slotPicker.unitHour', 'heure(s)') : unit === 'slot' ? t('settings.slotPicker.unitSlot', 'créneau(x)') : t('settings.slotPicker.unitDay', 'jour(s)');
      return {
        valid: false,
        reason: t('settings.slotPicker.maxExceeded', 'Maximum {{max}} {{unit}} autorisé(s) ({{current}}/{{max}})', { max: maxDuration, unit: label, current: selection.length }),
      };
    }
    // Vérifier consécutivité
    if (selection.length > 1) {
      for (let i = 0; i < selection.length - 1; i++) {
        const gap = new Date(selection[i + 1].start_at) - new Date(selection[i].end_at);
        // Generated slots are spaced by slotDuration + bufferBefore + bufferAfter,
        // so the gap between end of slot N and start of slot N+1 equals (bufferBefore + bufferAfter) minutes.
        // Allow that gap plus a small tolerance for rounding/serialization.
        const allowedGapMs = (bufferBefore + bufferAfter) * 60 * 1000 + 5000;
        if (gap > allowedGapMs) {
          return {
            valid: false,
            reason: t('settings.slotPicker.nonConsecutive', 'Slots not consecutive between {{a}} and {{b}}', { a: format(new Date(selection[i].end_at), 'HH:mm'), b: format(new Date(selection[i + 1].start_at), 'HH:mm') }),
          };
        }
      }
    }
    return { valid: true, reason: null };
  }, [minDuration, maxDuration, unit, bufferBefore, bufferAfter]);

  // ── Notifier le parent à chaque changement de sélection ──────────────────
  // Passer les ISO strings tels quels pour éviter toda conversion de fuseau horaire
  useEffect(() => {
    const { valid, reason } = validate(selectedSlots);
    // Passer les ISO strings tels quels (sans Z, wall-clock) — pas de new Date() qui convertirait en UTC
    const start = selectedSlots.length > 0 ? selectedSlots[0].start_at : null;
    const end   = selectedSlots.length > 0 ? selectedSlots[selectedSlots.length - 1].end_at : null;
    onSelectionChange?.({ selectedSlots, startDate: start, endDate: end, valid, reason });
  }, [selectedSlots, validate, onSelectionChange]);

  // ── Clic sur un créneau ───────────────────────────────────────────────────
  const handleSlotClick = (slot) => {
    if (!slot.available) return;

    setSelectedSlots((prev) => {
      const idx = prev.findIndex((s) => s.start_at === slot.start_at && s.end_at === slot.end_at);
      let next;
      if (idx !== -1) {
        // Déselectionner
        next = prev.filter((_, i) => i !== idx);
      } else {
        // Si minDuration = 1 et maxDuration = 1 → simple remplacement
        if (minDuration === 1 && maxDuration === 1) {
          next = [slot];
        } else {
          next = [...prev, slot].sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
        }
      }
      return next;
    });
  };

  // ── Navigation semaine ────────────────────────────────────────────────────
  // Trouver le premier jour non passé ET ouvert dans une semaine donnée
  const firstOpenDay = (weekAnchor) =>
    Array.from({ length: 7 }, (_, i) => addDays(weekAnchor, i))
      .find(d => (!isPast(startOfDay(d)) || isSameDay(d, new Date())) && !isDayClosed(d)) || null;

  const prevWeek = () => {
    const newStart = subDays(weekStart, 7);
    setWeekStart(newStart);
    const open = firstOpenDay(newStart);
    if (open) setActiveDay(open);
  };
  const nextWeek = () => {
    const newStart = addDays(weekStart, 7);
    setWeekStart(newStart);
    const open = firstOpenDay(newStart);
    if (open) setActiveDay(open);
  };

  // ── Labels ────────────────────────────────────────────────────────────────
  const unitLabel  = (n) => {
    const lbl = unit === 'hour' ? 'h' : unit === 'slot' ? ` cr.` : ' j.';
    return `${n}${lbl}`;
  };

  const durLabel = `${slotDuration >= 60 ? slotDuration / 60 + 'h' : slotDuration + 'min'}`;

  // ── Calcul du badge de statut ─────────────────────────────────────────────
  const { valid: selValid, reason: selReason } = validate(selectedSlots);
  const selStart = selectedSlots[0];
  const selEnd   = selectedSlots[selectedSlots.length - 1];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="slot-picker">

      {/* ── En-tête ── */}
      <div className="slot-picker__header">
          <button className="slot-picker__nav-btn" onClick={prevWeek} aria-label={t('settings.slotPicker.prevWeek','Previous week')}>‹</button>
        <div className="slot-picker__title">
          <span>⏰</span>
          <h3>{format(weekStart, 'MMMM yyyy', { locale: dateLocale })}</h3>
        </div>
        <button className="slot-picker__nav-btn" onClick={nextWeek} aria-label={t('settings.slotPicker.nextWeek','Next week')}>›</button>
      </div>

      {/* ── Contraintes ── */}
        <div className="slot-picker__constraints">
        <span className="slot-picker__constraint-tag">⚡ {durLabel}/{t('settings.slotPicker.perSlotShort','cr')}</span>
        <span className="slot-picker__constraint-tag">
          🎯 {minDuration && maxDuration
            ? `${unitLabel(minDuration)} – ${unitLabel(maxDuration)}`
            : minDuration
            ? `${t('settings.slotPicker.minLabel','min')} ${unitLabel(minDuration)}`
            : t('settings.slotPicker.free','Libre')}
        </span>
        {openingHours.length > 0 && (
          <span className="slot-picker__constraint-tag">🕐 {t('settings.slotPicker.openingRanges','Plages horaires')}</span>
        )}
        {(bufferBefore > 0 || bufferAfter > 0) && (
          <span className="slot-picker__constraint-tag">
            🛡 {bufferBefore > 0 ? `+${bufferBefore}′` : ''}{bufferBefore > 0 && bufferAfter > 0 ? ' / ' : ''}{bufferAfter > 0 ? `+${bufferAfter}′` : ''}
          </span>
        )}
        {isCapacity && (
          <span className="slot-picker__constraint-tag">👥 {t('settings.slotPicker.sharedPlaces','Places partagées')}</span>
        )}
        {!isCapacity && (
          <span className="slot-picker__constraint-tag">🔒 {t('settings.slotPicker.exclusive','Exclusif')}</span>
        )}
      </div>

      {/* ── Navigation jours ── */}
      <div className="slot-picker__week-nav">
        <button className="slot-picker__nav-btn" onClick={prevWeek} aria-label="Semaine précédente" style={{ fontSize: 13 }}>‹</button>
        <div className="slot-picker__week-days">
          {weekDays.map((day, i) => {
            const isPastDay   = isPast(startOfDay(day)) && !isSameDay(day, new Date());
            const isClosed    = isDayClosed(day);
            const isDisabled  = isPastDay || isClosed;
            const isActive    = isSameDay(day, activeDay);
            const isTodayDay  = isToday(day);
            return (
              <button
                key={i}
                className={[
                  'slot-picker__day-btn',
                  isActive   ? 'slot-picker__day-btn--active'  : '',
                  isTodayDay ? 'slot-picker__day-btn--today'   : '',
                  isPastDay  ? 'slot-picker__day-btn--past'    : '',
                  isClosed   ? 'slot-picker__day-btn--closed'  : '',
                ].join(' ')}
                onClick={() => !isDisabled && setActiveDay(day)}
                disabled={isDisabled}
                title={isClosed ? 'Fermé ce jour' : undefined}
              >
                <span className="slot-picker__day-name">{DAYS_SHORT[i]}</span>
                <span className="slot-picker__day-num">{format(day, 'd')}</span>
                {!isDisabled && <span className="slot-picker__day-dot" />}
              </button>
            );
          })}
        </div>
        <button className="slot-picker__nav-btn" onClick={nextWeek} aria-label="Semaine suivante" style={{ fontSize: 13 }}>›</button>
      </div>

      {/* ── Jour actif affiché ── */}
      <div className="slot-picker__active-date">
          <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#374151' }}>
          📅 {format(activeDay, 'EEEE d MMMM yyyy', { locale: dateLocale })}
        </span>
      </div>

      {/* ── Corps : grille de créneaux ── */}
      <div className="slot-picker__body">
        {loading ? (
            <div className="slot-picker__loading">
            <div className="slot-picker__loading-spinner" />
            <span style={{ fontSize: '0.85rem' }}>{t('settings.slotPicker.loading','Chargement des créneaux…')}</span>
          </div>
        ) : error ? (
          <div className="slot-picker__empty">
            <div className="slot-picker__empty-icon">⚠️</div>
            <p>{error}</p>
          </div>
        ) : slots.length === 0 ? (
          <div className="slot-picker__empty">
            <div className="slot-picker__empty-icon">😴</div>
            <p>{t('settings.slotPicker.noSlots','Aucun créneau disponible ce jour')}</p>
          </div>
        ) : (
          <div className="slot-picker__grid">
            {slots.map((slot) => {
              const start     = new Date(slot.start_at);
              const end       = new Date(slot.end_at);
              // Block past slots (allow small grace for clock skew)
              const nowMs = Date.now();
              const isPastSlot = start.getTime() < (nowMs - 60 * 1000);
              const isSelected = selectedSlots.some(s => s.start_at === slot.start_at && s.end_at === slot.end_at);
              const isInRange  = !isSelected && selectedSlots.length > 1 &&
                new Date(slot.start_at) > new Date(selectedSlots[0].start_at) &&
                new Date(slot.end_at)   < new Date(selectedSlots[selectedSlots.length - 1].end_at);

              let cardClass = 'slot-card';
              if (!slot.available || isPastSlot) cardClass += ' slot-card--unavailable';
              else if (isSelected)  cardClass += ' slot-card--selected';
              else if (isInRange)   cardClass += ' slot-card--in-range';

              return (
                <button
                  key={`${slot.start_at}-${slot.end_at}`}
                  className={cardClass}
                  onClick={() => handleSlotClick(slot)}
                  disabled={!slot.available || isPastSlot}
                    title={
                    isPastSlot
                      ? t('settings.slotPicker.pastSlot','Créneau déjà passé')
                      : !slot.available
                        ? t('settings.slotPicker.slotUnavailable','Créneau non disponible')
                        : `${slot.start_at.substring(11, 16)} – ${slot.end_at.substring(11, 16)}`
                  }
                >
                  {isSelected && <span className="slot-card__check">✓</span>}

                  <span className="slot-card__time">
                    {slot.start_at.substring(11, 16)}<br />
                    <span style={{ fontWeight: 400, fontSize: '0.7rem', color: 'inherit', opacity: 0.75 }}>
                      → {slot.end_at.substring(11, 16)}
                    </span>
                  </span>

                  {typeof slot.price === 'number' && slot.price > 0 && (
                    <span className="slot-card__price">{formatMoney(slot.price)}</span>
                  )}

                  {isCapacity && typeof slot.remaining_seats === 'number' && (
                    <span className="slot-card__seats">
                      {slot.remaining_seats > 0
                        ? `${slot.remaining_seats} ${t('settings.slotPicker.plShort','pl.')}`
                        : t('settings.slotPicker.full','Complet')}
                    </span>
                  )}

                  {!slot.available && (
                    <span className="slot-card__unavailable-label">{t('settings.slotPicker.indispo','Indispo')}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Pied : récapitulatif sélection ── */}
      {selectedSlots.length > 0 && (
        <div className="slot-picker__footer">
          <div className="slot-picker__selection-summary">
            <span className="slot-picker__sel-date" style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 2 }}>
              {format(activeDay, 'EEEE d MMMM', { locale: dateLocale })}
            </span>
            <span className="slot-picker__sel-time">
              {format(new Date(selStart.start_at), 'HH:mm')}
              {selectedSlots.length > 1
                ? ` → ${format(new Date(selEnd.end_at), 'HH:mm')}`
                : ` → ${format(new Date(selStart.end_at), 'HH:mm')}`}
              {selectedSlots.length > 1 && (
                <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 4 }}>
                  ({t('sidebar.form.slotCount', { count: selectedSlots.length })})
                </span>
              )}
            </span>
            <div className="slot-picker__sel-badges">
              {selValid ? (
                <span className="slot-picker__badge slot-picker__badge--ok">✓ {t('settings.slotPicker.valid','Valide')}</span>
              ) : (
                <span className="slot-picker__badge slot-picker__badge--warn">{selReason}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
